import { FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { JsonMetadataField } from '../../components/metadata/JsonMetadataField';
import { ActiveStatusBadge } from '../../components/ui/ActiveStatusBadge';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { FieldLabel } from '../../components/ui/FieldLabel';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { ListPaginationBar } from '../../components/ui/ListPaginationBar';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import {
  BuyerPersona,
  BuyerPersonaSeniority,
  BuyerPersonaUpsertPayload,
  createBuyerPersona,
  deleteBuyerPersona,
  downloadBuyerPersonaImportTemplate,
  getBuyerPersonaImportStatus,
  importBuyerPersonas,
  listBuyerPersonas,
  updateBuyerPersona,
} from '../../services/buyerPersonasApi';
import { buyerPersonaFieldHelp } from './buyerPersonaFieldHelp';

function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

const SENIORITY_OPTIONS: { value: BuyerPersonaSeniority; label: string }[] = [
  { value: 'IndividualContributor', label: 'Contribuidor individual' },
  { value: 'Manager', label: 'Gerente' },
  { value: 'Director', label: 'Diretor' },
  { value: 'VP', label: 'VP' },
  { value: 'CLevel', label: 'C-Level' },
  { value: 'Other', label: 'Outro' },
];

type BuyerPersonaFormState = Omit<BuyerPersonaUpsertPayload, 'sortOrder'>;

const emptyForm: BuyerPersonaFormState = {
  name: '',
  description: '',
  jobTitle: '',
  seniority: 'Manager',
  painPoints: '',
  goals: '',
  demographicsJson: '',
  isActive: true,
};

function seniorityLabel(value: string): string {
  return SENIORITY_OPTIONS.find((item) => item.value === value)?.label ?? value;
}

export function BuyerPersonasPage() {
  const { canWriteModule } = usePermissions();
  const { confirm } = useConfirmDialog();
  const canWrite = canWriteModule('sales.buyer-personas');
  const [items, setItems] = useState<BuyerPersona[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<BuyerPersonaFormState>(emptyForm);
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } = useCollapsibleItemForm();
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  async function loadData(targetPage = page): Promise<void> {
    setLoading(true);
    try {
      const result = await listBuyerPersonas({
        page: targetPage,
        pageSize,
        search: search || undefined,
      });
      setItems(result.items);
      setTotalCount(result.totalCount);
      setPage(result.page);
    } catch {
      toast.error('Falha ao carregar personas.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  function resetForm(): void {
    setEditingId(null);
    setForm(emptyForm);
  }

  function startEdit(persona: BuyerPersona): void {
    expandForm();
    setEditingId(persona.id);
    setForm({
      name: persona.name,
      description: persona.description,
      jobTitle: persona.jobTitle,
      seniority: persona.seniority,
      painPoints: persona.painPoints,
      goals: persona.goals,
      demographicsJson: persona.demographicsJson ?? '',
      isActive: persona.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const editingItem = editingId ? items.find((item) => item.id === editingId) : null;
    const payload: BuyerPersonaUpsertPayload = {
      ...form,
      sortOrder: editingItem?.sortOrder ?? 0,
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateBuyerPersona(editingId, payload);
        toast.success('Persona atualizada.');
        resetForm();
      } else {
        await createBuyerPersona(payload);
        toast.success('Persona criada.');
        handleCreateSuccess(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao salvar persona.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(persona: BuyerPersona): Promise<void> {
    if (!canWrite) return;

    const accepted = await confirm({
      title: 'Excluir persona',
      message: `Deseja excluir "${persona.name}"? Produtos vinculados perderão este vínculo.`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!accepted) return;

    try {
      await deleteBuyerPersona(persona.id);
      toast.success('Persona excluída.');
      if (editingId === persona.id) {
        collapseForm(resetForm);
      }
      await loadData(page);
    } catch {
      toast.error('Falha ao excluir persona.');
    }
  }

  async function handleDownloadTemplate(): Promise<void> {
    try {
      const blob = await downloadBuyerPersonaImportTemplate();
      triggerBlobDownload(blob, 'personas-template.csv');
      toast.success('Template baixado.');
    } catch {
      toast.error('Falha ao baixar template.');
    }
  }

  async function pollImportStatus(importId: string): Promise<void> {
    for (let attempt = 0; attempt < 30; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        const status = await getBuyerPersonaImportStatus(importId);
        if (status.status === 'Completed') {
          toast.success(
            `Importação concluída: ${status.createdCount} criadas, ${status.updatedCount} atualizadas.`,
          );
          await loadData(page);
          return;
        }
        if (status.status === 'Failed') {
          toast.error(status.errorSummary || 'Importação falhou.');
          return;
        }
      } catch {
        // Continua tentando enquanto o job processa.
      }
    }
    toast.info('Importação ainda em andamento. Atualize a lista quando concluir.');
  }

  async function handleImportFile(file: File | undefined): Promise<void> {
    if (!file || !canWrite) return;
    setImporting(true);
    try {
      const queued = await importBuyerPersonas(file);
      toast.success(
        queued.hangfireJobId
          ? `Importação enfileirada (Hangfire ${queued.hangfireJobId}).`
          : 'Importação enfileirada.',
      );
      void pollImportStatus(queued.importId);
    } catch {
      toast.error('Falha ao enviar arquivo de importação.');
    } finally {
      setImporting(false);
      if (importInputRef.current) {
        importInputRef.current.value = '';
      }
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Personas compradoras"
        description="ICP estruturado — cargo, senioridade, dores e dimensões firmográficas."
        actions={
          <div className="erp-btn-actions">
            <button
              type="button"
              className="erp-btn-secondary"
              onClick={() => void handleDownloadTemplate()}
            >
              Template CSV
            </button>
            {canWrite ? (
              <>
                <input
                  ref={importInputRef}
                  type="file"
                  accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  className="hidden"
                  onChange={(event) => void handleImportFile(event.target.files?.[0])}
                />
                <button
                  type="button"
                  className="erp-btn-secondary"
                  disabled={importing}
                  onClick={() => importInputRef.current?.click()}
                >
                  {importing ? 'Importando...' : 'Importar'}
                </button>
              </>
            ) : null}
            <Link to="/sales/products" className="erp-link-back">
              ← Voltar para produtos
            </Link>
          </div>
        }
      />

      {canWrite ? (
        <CollapsibleFormSection
          title={editingId ? 'Editar persona' : 'Nova persona'}
          expanded={formExpanded}
          onToggle={() => toggleOrCancel(resetForm)}
          editingSummary={
            editingId ? `Editando ${form.name || 'persona selecionada'} — clique para expandir` : undefined
          }
          createSummary="Clique para expandir e cadastrar uma nova persona"
          isEditing={!!editingId}
        >
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="erp-form px-4 pb-4 pt-3"
          >
            <div className="erp-form-grid">
            <label className="erp-label">
              <FieldLabel {...buyerPersonaFieldHelp.name} required>
                Nome
              </FieldLabel>
              <input
                className="erp-input"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                required
              />
            </label>
            <label className="erp-label">
              <FieldLabel {...buyerPersonaFieldHelp.jobTitle}>Cargo</FieldLabel>
              <input
                className="erp-input"
                value={form.jobTitle ?? ''}
                onChange={(event) => setForm({ ...form, jobTitle: event.target.value })}
                placeholder="Ex.: Head de Operações"
              />
            </label>
            <label className="erp-label">
              <FieldLabel {...buyerPersonaFieldHelp.seniority} required>
                Senioridade
              </FieldLabel>
              <select
                className="erp-input"
                value={form.seniority ?? 'Manager'}
                onChange={(event) =>
                  setForm({ ...form, seniority: event.target.value as BuyerPersonaSeniority })
                }
              >
                {SENIORITY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <label className="erp-label mt-3 block">
            <FieldLabel {...buyerPersonaFieldHelp.description}>Descrição</FieldLabel>
            <textarea
              className="erp-input mt-1 min-h-[60px]"
              value={form.description ?? ''}
              onChange={(event) => setForm({ ...form, description: event.target.value })}
            />
          </label>
          <label className="erp-label mt-3 block">
            <FieldLabel {...buyerPersonaFieldHelp.painPoints}>Dores (pain points)</FieldLabel>
            <textarea
              className="erp-input mt-1 min-h-[60px]"
              value={form.painPoints ?? ''}
              onChange={(event) => setForm({ ...form, painPoints: event.target.value })}
            />
          </label>
          <label className="erp-label mt-3 block">
            <FieldLabel {...buyerPersonaFieldHelp.goals}>Objetivos</FieldLabel>
            <textarea
              className="erp-input mt-1 min-h-[60px]"
              value={form.goals ?? ''}
              onChange={(event) => setForm({ ...form, goals: event.target.value })}
            />
          </label>
          <div className="mt-3">
            <JsonMetadataField
              label="Dimensões firmográficas"
              description="Regiões, porte, setores e outros critérios descritivos da persona."
              value={form.demographicsJson}
              onChange={(demographicsJson) => setForm({ ...form, demographicsJson })}
            />
          </div>
          <label className="erp-label-inline mt-3">
            <input
              type="checkbox"
              checked={form.isActive ?? true}
              onChange={(event) => setForm({ ...form, isActive: event.target.checked })}
            />
            Ativo
          </label>
          <FormActionBar
            onCancel={() => collapseForm(resetForm)}
            cancelLabel="Cancelar"
            className="erp-btn-actions mt-4"
          >
            <button type="submit" disabled={saving} className="erp-btn-primary">
              {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Criar'}
            </button>
          </FormActionBar>
          </form>
        </CollapsibleFormSection>
      ) : null}

      <div className="mb-4 flex flex-wrap gap-2">
        <input
          className="erp-input max-w-md"
          placeholder="Buscar por nome..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void loadData(1);
            }
          }}
        />
        <button type="button" className="erp-btn-secondary" onClick={() => void loadData(1)}>
          Buscar
        </button>
      </div>

      {loading ? (
        <LoadingSpinner label="Carregando personas..." />
      ) : (
        <div className="erp-card overflow-x-auto">
          <table className="erp-table w-full">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Cargo</th>
                <th>Senioridade</th>
                <th>Status</th>
                {canWrite ? <th /> : null}
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={canWrite ? 5 : 4} className="text-center text-secondary-500">
                    Nenhuma persona cadastrada.
                  </td>
                </tr>
              ) : (
                items.map((persona) => (
                  <tr key={persona.id}>
                    <td>{persona.name}</td>
                    <td>{persona.jobTitle || '—'}</td>
                    <td>{seniorityLabel(persona.seniority)}</td>
                    <td>
                      <ActiveStatusBadge active={persona.isActive} />
                    </td>
                    {canWrite ? (
                      <td className="text-right">
                        <EditIconButton onClick={() => startEdit(persona)} label="Editar" />
                        <DeleteIconButton onClick={() => void handleDelete(persona)} label="Excluir" />
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      <ListPaginationBar
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPrevious={() => setPage((current) => Math.max(1, current - 1))}
        onNext={() => setPage((current) => Math.min(totalPages, current + 1))}
      />
    </PageShell>
  );
}
